import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import APIUtil from 'util/APIUtil';

import {ProcessFlowchartEdgeType, ProcessFlowchartNodeType} from './processFlowchartConfigEditorConstants';
import {TaskSchemaStaticNodeData, TaskSchemaNodeData} from './processFlowchartConfigEditorNodeTypes';

export const NODE_IDENTIFIER_PREFIX = 'node';
export const FIELD_IDENTIFIER_PREFIX = 'field';

export function generateTaskSchemaNodeIdentifier(taskSchema: {id: string}): string {
  const {id} = taskSchema;
  // Task schemas can be repeated in a flowchart, add a unique identifier to distinguish nodes
  const hash = uuidv4();
  return `${NODE_IDENTIFIER_PREFIX}:${id}:${hash}`;
}

export function generateLogicNodeIdentifier(nodeType: ProcessFlowchartNodeType): string {
  const hash = uuidv4();
  return `${NODE_IDENTIFIER_PREFIX}:${nodeType}:${hash}`;
}

/**
 * Creates the client ID for a field on a flowchart node.
 *
 * The client ID has the form <fieldCategory>:<fieldName>, e.g. TASK:Field1 for an input field or
 * OUTPUT:Field2 for an output field.
 *
 * @param fieldName the name of the field.
 * @param isOutput whether the field is an output field.
 * @returns the client ID for the field.
 */
export function makeFieldClientId(fieldName: string, isOutput: boolean): string {
  return `${isOutput ? 'OUTPUT' : 'TASK'}:${fieldName}`;
}

/**
 * Gets the client ID and section name of a node's field from a handle ID for the field.
 *
 * The handle ID has format field:<sectionName>:<fieldCategory>:<fieldName>-<handleType>, e.g.
 * field:INPUT:TASK:Field1-source
 *
 * @param handleId - the handle ID.
 * @returns the client ID and section name of the field.
 */
export function getSchemaFieldClientInfoFromHandle(handleId: string): {
  fieldClientId: string;
  sectionName: string;
} {
  const identifierParts = handleId.split(':');
  assert(identifierParts.length === 4, `Invalid handleId: ${handleId}`);
  const [_, sectionName, fieldCategory, fieldIdAndHandleType] = identifierParts;
  const fieldId = fieldIdAndHandleType.split('-')[0];
  return {fieldClientId: `${fieldCategory}:${fieldId}`, sectionName};
}

export function getSchemaFieldIdFromRowIdentifier(rowIdentifier: string): string {
  const identifierParts = rowIdentifier.split(':');
  assert(identifierParts.length === 3, `Invalid rowIdentifier: ${rowIdentifier}`);
  const fieldId = identifierParts[2];
  assert(APIUtil.isSchemaField(fieldId), `Invalid fieldId: ${fieldId}`);
  return fieldId;
}

/**
 * Gets the field ID and section name from a field client ID and node.
 *
 * The field ID is retrieved from the row information for the field on the node.
 *
 * @param fieldClientInfo - the client ID and section name of the field.
 * @param node - the node that contains the field.
 * @returns the field ID and section name.
 */
export function getFieldInfoFromClientInfoAndNode(
  fieldClientInfo: {fieldClientId: string; sectionName: string},
  node: TaskSchemaStaticNodeData | TaskSchemaNodeData
): {fieldId: string; sectionName: string} {
  const {fieldClientId, sectionName} = fieldClientInfo;
  const section = node.sections.find((section) => section.sectionName === sectionName);
  assert(section != null, `Section ${sectionName} not found in node ${node.id}`);

  const row = section.sectionRows.find((row) => row.clientId === fieldClientId);
  assert(
    row != null,
    `Row for field with clientId ${fieldClientId} not found in section ${sectionName} of node ${node.id}`
  );
  return {fieldId: getSchemaFieldIdFromRowIdentifier(row.id), sectionName};
}

export function generateTaskSchemaFieldRowIdentifier(fieldId: string, sectionName: string): string {
  return `${FIELD_IDENTIFIER_PREFIX}:${sectionName}:${fieldId}`;
}

export function makeDataFlowEdgeHandle(
  fieldClientId: string,
  taskSchemaNodeSection: string,
  handleType: 'source' | 'target'
): string {
  return `${generateTaskSchemaFieldRowIdentifier(fieldClientId, taskSchemaNodeSection)}-${handleType}`;
}

export function doesDataFlowEdgeHandleReferenceRow(dataFlowEdgeHandle: string, rowClientId: string): boolean {
  return rowClientId === getSchemaFieldClientInfoFromHandle(dataFlowEdgeHandle).fieldClientId;
}

export function getEdgeTypeFromHandleId(handleId: string): ProcessFlowchartEdgeType {
  if (handleId.startsWith(FIELD_IDENTIFIER_PREFIX)) {
    return ProcessFlowchartEdgeType.DATA_FLOW;
  }
  return ProcessFlowchartEdgeType.CONTROL_FLOW;
}
